import DictionaryApiService from "../../admin/system/integrate/DictionaryApiService";
import WiktionaryAttribution from "../../admin/pzl_common/WiktionaryAttribution";
import { Box, Divider, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Logger from "../../services/Logger";
import { usePagesTranslation } from "../locales";

/** Single-line-ish collapse + char cap + ellipsis (Unicode …). */
function clipText(text, maxLen) {
    const raw = String(text || "")
        .replace(/(\d+\.\s)/g, " $1")
        .replace(/\s+/g, " ")
        .trim();
    if (raw.length <= maxLen) return raw;
    const cut = raw.slice(0, maxLen);
    const lastSpace = cut.lastIndexOf(" ");
    const base = lastSpace > Math.min(32, maxLen * 0.4) ? cut.slice(0, lastSpace) : cut;
    return `${base.trimEnd()}…`;
}

/**
 * Read-only definitions for Word of the Day: no Paper, no max-height scroll box.
 * Word title and pronunciation live in the parent (WordOfDayMain).
 */
export default function WordOfDayMeaning({ selectedWord, language = "english" }) {
    const { t } = usePagesTranslation();
    const [wordData, setWordData] = useState(null);
    const [loading, setLoading] = useState(false);

    const lang = language === "french" ? "french" : "english";

    useEffect(() => {
        const w = String(selectedWord || "").trim();
        if (!w) {
            setWordData(null);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const meaningData = await DictionaryApiService.getWordDefinition(w, lang);
                if (!cancelled) setWordData(meaningData);
            } catch (error) {
                Logger.error("[WordOfDayMeaning] Error fetching definition:", error);
                if (!cancelled) setWordData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selectedWord, lang]);

    if (!String(selectedWord || "").trim()) {
        return null;
    }

    if (loading) {
        return (
            <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                    {t("wordOfDay.definition-loading")}
                </Typography>
            </Box>
        );
    }

    if (!wordData || wordData.length === 0) {
        return (
            <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                    {t("wordOfDay.no-definition")}
                </Typography>
            </Box>
        );
    }

    const wordEntry = wordData[0];
    if (!wordEntry?.meanings?.length) {
        return (
            <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                    {t("wordOfDay.no-definition")}
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 0.5 }}>
            {wordEntry.phonetics && wordEntry.phonetics.length > 0 && wordEntry.phonetics[0].text ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: "italic" }}>
                    {wordEntry.phonetics[0].text}
                </Typography>
            ) : null}

            {wordEntry.meanings.map((meaning, meaningIndex) => (
                <Box key={meaningIndex} sx={{ mb: 2 }}>
                    {meaningIndex > 0 ? <Divider sx={{ my: 1.5, opacity: 0.35 }} /> : null}
                    {meaning.partOfSpeech ? (
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: "bold", color: "secondary.main", mb: 1, textTransform: "capitalize" }}
                        >
                            {meaning.partOfSpeech}
                        </Typography>
                    ) : null}
                    {meaning.definitions.map((def, defIndex) => (
                        <Box key={defIndex} sx={{ mb: 1.5, pl: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 0.5,
                                    lineHeight: 1.6,
                                    color: "text.primary",
                                }}
                            >
                                {clipText(def.definition, 200)}
                            </Typography>
                            {def.example ? (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontStyle: "italic",
                                        color: "text.secondary",
                                        display: "block",
                                        mt: 0.5,
                                        pl: 1,
                                        borderLeft: "2px solid",
                                        borderColor: "divider",
                                    }}
                                >
                                    "{clipText(def.example, 100)}"
                                </Typography>
                            ) : null}
                            {def.synonyms && def.synonyms.length > 0 ? (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        display: "block",
                                        color: "text.secondary",
                                        mt: 0.5,
                                        fontSize: "0.7rem",
                                    }}
                                >
                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                        Synonyms:
                                    </Box>{" "}
                                    {clipText(def.synonyms.join(", "), 80)}
                                </Typography>
                            ) : null}
                            {defIndex < meaning.definitions.length - 1 ? (
                                <Divider sx={{ my: 0.5, opacity: 0.3 }} />
                            ) : null}
                        </Box>
                    ))}
                </Box>
            ))}

            <Box sx={{ mt: 1 }}>
                <WiktionaryAttribution />
            </Box>
        </Box>
    );
}
